import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { Fragment, useMemo, useState } from "react";
import {
  ArrowLeft,
  CaretDown,
  CaretLeft,
  CaretRight,
  ChartLine,
  FlowArrow,
  ListBullets,
} from "@phosphor-icons/react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveSankey } from "@nivo/sankey";
import { useTranslation } from "react-i18next";
import { DimensionDropdown, QuarterDropdown, YearDropdown } from "../FootPrint";
import YoYChip from "../YoYChip";
import { useBreakdownLevel } from "../../../../../hooks/useBreakdownLevel";
import {
  BY_DIMENSION_PAGE_SIZE,
  dimensionFilterParams,
} from "../../../../../utils/breakdownLevel";
import {
  useGetCompanyDimensionReports,
  useGetCompanyTotalEmissionsReports,
  useGetDownstreamTransportationAndDistribution,
  useGetReport,
  useGetScopeThreeGroupCuts,
  useGetScopeThreeModeCuts,
  useGetWasteGeneratedInOperations,
  useGetQuarterlyCompanyTotalEmissions,
  useGetQuarterlyMultiYearCompanyTotalEmissions,
} from "../../../../../hooks/query/reports";
import { useGetTotalEmissions } from "../../../../../hooks/query/emissions-insight/emissions-insights-query";
import QuartersSwitch, { quarterPeriodLabels } from "./QuartersSwitch";
import QuarterlyBreakdownTable, { useQuarterlyTableCompare } from "./QuarterlyBreakdownTable";
import ExpandCollapseControl from "./ExpandCollapseControl";
import { scopeThreeCategories } from "../../../CompanyEmissionsDashboard/components/helpers";
import { CATEGORY_KEY_TO_SCOPE_ENUM } from "../../../../OrganizationQuestionnaire/components/Categories/const";
import { axisBaselineLayer, chartTheme } from "../Consumption/chartTheme";
import { toSafariSafeSankey } from "../../../../../utils/sankey";
import SourceDataTasks from "./SourceDataTasks";

const TOTAL_ACCENT = "#5B1D99";

// Retry helper for report-dependent hooks: don't retry genuine 404s (no report for
// this period) but allow 2 retries on transient errors so a single network blip
// doesn't permanently cache an error and kill all cat5ReportId-dependent data
// until the user logs out and the React Query cache is cleared.
const retryNot404 = (count, error) => error?.response?.status !== 404 && count < 2;


const formatTco2 = (v) =>
  v == null || !Number.isFinite(Number(v))
    ? null
    : Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatGbp = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `£${(n / 1_000_000).toLocaleString("en-GB", { maximumFractionDigits: 1 })}m`;
  if (Math.abs(n) >= 1_000) return `£${(n / 1_000).toLocaleString("en-GB", { maximumFractionDigits: 0 })}k`;
  return `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
};

// Convert backend waste-activity enum values (UPPER_SNAKE_CASE) to the
// sentence-case label we want in the UI. Falls back to the raw value if
// it doesn't follow the enum convention.
const prettyActivity = (raw) => {
  if (typeof raw !== "string" || !raw) return "Unspecified";
  const lower = raw.replace(/[_-]+/g, " ").trim().toLowerCase();
  return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : raw;
};

// Column config for the Spend column rendered by CutTable. `format`
// overrides the default kWh-style number formatting.
const SPEND_COLUMN = { key: "spend", unit: "", label: "Spend (£)", format: formatGbp };

const ScopeThreeCategoryDetail = ({ year, setYear, quarter, setQuarter, dimension, setDimension }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });
  // null = whole year; the /reports endpoints expect "ALL".
  const reportQuarter = quarter || "ALL";

  const numberKey = searchParams.get("category");
  const cat = scopeThreeCategories.find((c) => c.numberKey === numberKey);
  // Backend ScopeCategoryKey name for this category (drives the real cut/breakdown lookups).
  const scopeEnum = cat ? CATEGORY_KEY_TO_SCOPE_ENUM[cat.translationKey] : null;
  const accent = cat?.color || TOTAL_ACCENT;
  const label = cat ? t(cat.translationKey) : "Category";

  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.set("detailView", "scopeThree");
    params.delete("category");
    setSearchParams(params);
  };

  // Wrap-around prev/next + jump-to-any. The previous of Cat 1 is Cat 15
  // and the next of Cat 15 is Cat 1, so the user never hits a dead end.
  const currentIdx = scopeThreeCategories.findIndex((c) => c.numberKey === numberKey);
  const prevCat = currentIdx >= 0
    ? scopeThreeCategories[(currentIdx - 1 + scopeThreeCategories.length) % scopeThreeCategories.length]
    : null;
  const nextCat = currentIdx >= 0
    ? scopeThreeCategories[(currentIdx + 1) % scopeThreeCategories.length]
    : null;
  const goToCategory = (targetNumberKey) => {
    const params = new URLSearchParams(searchParams);
    params.set("detailView", "scopeThreeCategory");
    params.set("category", targetNumberKey);
    setSearchParams(params);
  };

  // Real values for the current and previous year.
  const { data: currentScope3 } = useGetCompanyTotalEmissionsReports(
    { year, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: previousScope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 1, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: yMinus2Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 2, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: yMinus3Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 3, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: yMinus4Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 4, quarter: reportQuarter },
    { enabled: !!year }
  );

  const valueFor = (data) => {
    if (!data || !cat) return 0;
    return Number(data?.[`category${cat.numberKey}TotalCo2Emissions`]) || 0;
  };

  const current = valueFor(currentScope3);
  const previous = valueFor(previousScope3);
  const total = currentScope3?.scopeThreeTotalCo2Emissions || 0;
  const hasPrev = previous !== 0;
  const yoy = hasPrev ? ((current - previous) / previous) * 100 : null;
  const shareOfScope3 = total > 0 ? (current / total) * 100 : 0;

  // Cat 3 (Fuel- and energy-related activities) uses Scope 2's purchased
  // electricity as its consumption denominator — fetch the same /insights
  // payload Scope 2 uses and surface the location-based kWh on the Total
  // KPI card. Skipped for every other category so we don't make extra
  // requests.
  const isCat1 = cat?.numberKey === "One";
  const isCat3 = cat?.numberKey === "Three";
  const isCat5 = cat?.numberKey === "Five";
  const isCat6 = cat?.numberKey === "Six";
  const isCat7 = cat?.numberKey === "Seven";
  const isCat9 = cat?.numberKey === "Nine";
  const isCat11 = cat?.numberKey === "Eleven";

  // Per-category cut tabs. Cat 1/2 add supplier + product cuts; Cat 4/9 add a
  // transport-mode cut — all real, dimension-scoped (ESG-3942).
  const cutTabs = useMemo(() => cutTabsForCategory(cat), [cat]);
  const hasSupplierProductCut = cutTabs.includes("supplier") || cutTabs.includes("product");
  const hasModeCut = cutTabs.includes("transport") || cutTabs.includes("transportType");
  const { data: scope2CurrentYear } = useGetTotalEmissions(
    year,
    [],
    {
      enabled: !!year && isCat3,
      cacheTime: 60,
    },
    quarter
  );
  const purchasedElectricity = useMemo(() => {
    if (!isCat3) return null;
    const rows = scope2CurrentYear?.scopeOneAndTwoDetail?.electricity || [];
    const isLocation = (t) => String(t || "").toUpperCase().includes("LOCATION");
    return rows
      .filter((r) => isLocation(r.consumptionType))
      .reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);
  }, [isCat3, scope2CurrentYear]);

  // Cat 1 / Cat 5 / Cat 6 real data — pull the company-level report. For Cat 1 we read the water
  // usage section; for Cat 5 the wastewater section + paginated waste items; for Cat 6 the
  // businessTravel section (transportModeItems + per-(mode, subtype) items). The supplier/product and
  // transport-mode cuts need its id too — fetch it for them explicitly rather than relying on the
  // overview having cached it, or a direct load leaves those tables empty (ESG-4008). Endpoint returns
  // 404 for years with no data. retry skips 404s (no point retrying a missing report) but allows 2
  // retries on transient errors. staleTime: 0 overrides the app-wide 5-minute default (index.jsx) — this
  // page never unmounts as the user switches between categories, it only toggles `enabled` on this same
  // query, so a 5-minute staleTime meant one bad/empty response stayed cached (and un-refetchable by
  // any in-app navigation) until a full reload — which is why logging out and back in "fixed" it.
  const { data: cat5Report } = useGetReport(
    { year, quarter: reportQuarter },
    {
      enabled:
        !!year &&
        (isCat1 || isCat5 || isCat6 || isCat7 || isCat9 || isCat11 || hasSupplierProductCut || hasModeCut),
      retry: retryNot404,
      staleTime: 0,
    }
  );
  const cat5ReportId = cat5Report?.id;
  // ESG-4002: water usage is a real sibling section of the Cat 1 report, already folded into the
  // category Total (ReportPurchasedGoodsAndServicesCalculationService.setTotalCo2Emission adds it
  // unconditionally) — this just surfaces the figure that was already being counted, same pattern as
  // Cat 5's wastewater below.
  const cat1WaterUsage = cat5Report?.scopeThree?.purchasedGoodsAndServices?.waterUsageTreatment;
  const totalWaterUsageM3 = Number(cat1WaterUsage?.waterUsageConsumption) || 0;
  const totalWaterUsageTco2 = Number(cat1WaterUsage?.waterUsageEmission) || 0;
  const cat5Wastewater = cat5Report?.scopeThree?.wasteGeneratedInOperations?.wastewater;
  const totalWastewaterM3 = Number(cat5Wastewater?.wastewaterConsumption) || 0;
  const totalWastewaterTco2 = Number(cat5Wastewater?.wastewaterEmission) || 0;

  const { data: wasteItemsPage } = useGetWasteGeneratedInOperations(
    cat5ReportId,
    { page: 0, size: 1000 },
    { enabled: !!cat5ReportId && isCat5, retry: retryNot404, staleTime: 0 }
  );
  const wasteTypeRowsReal = useMemo(() => {
    const rows = wasteItemsPage?.content || [];
    if (!isCat5 || (rows.length === 0 && totalWastewaterTco2 <= 0)) return null;
    const byActivity = new Map();
    rows.forEach((r) => {
      const key = String(r.wasteActivity || "Unspecified");
      const existing = byActivity.get(key) || {
        name: prettyActivity(key),
        value: 0,
        wasteTonnes: 0,
      };
      existing.value += Number(r.co2Emissions) || 0;
      existing.wasteTonnes += Number(r.weight) || 0;
      byActivity.set(key, existing);
    });
    const built = Array.from(byActivity.values())
      .filter((r) => r.value > 0 || r.wasteTonnes > 0)
      // `consumption`/`unit` are what the table displays (per-row unit, ESG-4002); `wasteTonnes`
      // stays a tonnes-only field so totalWasteTonnesReal below (used to apportion the Country tab's
      // Waste (t) column) never picks up a non-tonnes figure.
      .map((r) => ({ ...r, consumption: r.wasteTonnes, unit: "t" }))
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
    // Wastewater is a separate stream from the same Cat 5 report, not a waste type — it has no
    // per-activity tonnage, but it does have its own m³ consumption figure. Shown as its own row,
    // in its own unit, rather than a dedicated column that would be structurally zero for every
    // real activity row above (ESG-4002).
    if (totalWastewaterTco2 > 0) {
      built.push({
        name: "Wastewater",
        value: totalWastewaterTco2,
        wasteTonnes: 0,
        consumption: totalWastewaterM3,
        unit: "m³",
      });
      built.sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
    }
    return built;
  }, [isCat5, wasteItemsPage, totalWastewaterTco2, totalWastewaterM3]);
  const totalWasteTonnesReal = useMemo(() => {
    if (!wasteTypeRowsReal) return 0;
    return wasteTypeRowsReal.reduce((acc, r) => acc + (Number(r.wasteTonnes) || 0), 0);
  }, [wasteTypeRowsReal]);

  // Cat 6 (Business travel) and Cat 7 (Employee commuting) both ship a
  // mode-of-transport aggregate plus a per-(mode, subtype) item list on
  // the company report. The shapes differ slightly (field names + the
  // emissions field), so this builder takes the relevant section + the
  // emissions accessor + the mode-aggregate field name and returns the
  // unified rows the BusinessTravelModeTable consumes.
  const buildTravelModeRows = (section, modeAggField, itemEmissionField) => {
    if (!section) return null;
    const modeAggregates = section[modeAggField] || [];
    const subtypeItems = section.items || [];
    const subtypesByMode = new Map();
    subtypeItems.forEach((it) => {
      const mode = String(it.modeOfTransport || "unspecified").toLowerCase();
      if (!subtypesByMode.has(mode)) subtypesByMode.set(mode, []);
      subtypesByMode.get(mode).push(it);
    });
    const built = modeAggregates.map((m) => {
      const modeKey = String(m.modeOfTransport || "unspecified").toLowerCase();
      const subRows = (subtypesByMode.get(modeKey) || [])
        .map((it) => {
          const subKey = String(it.transportSubtype || "");
          const value = Number(it[itemEmissionField]) || 0;
          return { name: prettyActivity(subKey || "Unspecified"), value };
        })
        .filter((r) => r.value > 0)
        .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
      const value = Number(m.co2Emissions) || 0;
      return { name: prettyActivity(modeKey || "Unspecified"), value, subRows };
    });
    return built
      .filter((r) => r.value > 0 || (r.subRows && r.subRows.length > 0))
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  };

  const businessTravelRows = useMemo(() => {
    if (!isCat6) return null;
    return buildTravelModeRows(
      cat5Report?.scopeThree?.businessTravel,
      "transportModeItems",
      "co2Emissions",
    );
  }, [isCat6, cat5Report]);

  // Cat 7 (Employee commuting) — same shape as Cat 6 but with a slightly
  // different DTO: aggregates field is `transportModes` (not
  // `transportModeItems`), and per-row emissions ride on `totalEmission`.
  const employeeCommutingRows = useMemo(() => {
    if (!isCat7) return null;
    return buildTravelModeRows(
      cat5Report?.scopeThree?.employeeCommuting,
      "transportModes",
      "totalEmission",
    );
  }, [isCat7, cat5Report]);

  const travelModeRows = isCat7 ? employeeCommutingRows : businessTravelRows;

  // Cat 9 (Downstream transportation & distribution) — fetch the per-item
  // list from /reports/{id}/scope-three/downstream-transportation-distribution/
  // items. Unlike Cat 6/7 (where the company DTO doesn't expose distance),
  // each Cat 9 item ships real `value` + `unit` alongside `co2Emissions`,
  // plus a 3-level type/subtype hierarchy. We aggregate by typeOfTransport
  // for the top row and subtypeOfTransport for the expansion. Items where
  // multiple unit codes appear under the same node fall back to "mixed"
  // since we can't sum across e.g. t·km and km.
  const { data: cat9ItemsPage } = useGetDownstreamTransportationAndDistribution(
    cat5ReportId,
    { page: 0, size: 1000 },
    { enabled: !!cat5ReportId && isCat9, retry: retryNot404, staleTime: 0 },
  );
  const cat9TransportRows = useMemo(() => {
    if (!isCat9) return null;
    const items = cat9ItemsPage?.content || [];
    if (items.length === 0) return null;
    const modeMap = new Map();
    items.forEach((it) => {
      const modeName = it.typeOfTransport?.name || "Unspecified";
      const subName = it.subtypeOfTransport?.name || "Unspecified";
      const unitCode = it.unit?.code || "";
      const value = Number(it.value) || 0;
      const emissions = Number(it.co2Emissions) || 0;
      const mode = modeMap.get(modeName) || {
        name: modeName,
        value: 0,
        consumption: 0,
        unit: unitCode || null,
        unitMixed: false,
        subRows: new Map(),
      };
      mode.value += emissions;
      mode.consumption += value;
      if (unitCode && mode.unit && mode.unit !== unitCode) mode.unitMixed = true;
      else if (unitCode && !mode.unit) mode.unit = unitCode;

      const sub = mode.subRows.get(subName) || {
        name: subName,
        value: 0,
        consumption: 0,
        unit: unitCode || null,
        unitMixed: false,
      };
      sub.value += emissions;
      sub.consumption += value;
      if (unitCode && sub.unit && sub.unit !== unitCode) sub.unitMixed = true;
      else if (unitCode && !sub.unit) sub.unit = unitCode;
      mode.subRows.set(subName, sub);
      modeMap.set(modeName, mode);
    });
    return Array.from(modeMap.values()).map((mode) => ({
      name: mode.name,
      value: mode.value,
      consumption: mode.consumption,
      unit: mode.unit,
      unitMixed: mode.unitMixed,
      subRows: Array.from(mode.subRows.values())
        .filter((s) => s.value > 0 || s.consumption > 0)
        .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name)),
    }))
      .filter((m) => m.value > 0 || m.consumption > 0)
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  }, [isCat9, cat9ItemsPage]);

  // Cat 11 (Use of sold products) — pull per-product items from the
  // company report. Per-item gas/electricity consumption is given per
  // item per year; lifetime company-wide consumption =
  // numberOfItemsSold × averageExpectedLifeSpan × consumption-per-item.
  // The DTO ships those raw fields (we extended the backend mapper) so
  // we can sum them locally without a separate endpoint.
  const cat11ProductRows = useMemo(() => {
    if (!isCat11) return null;
    const items = cat5Report?.scopeThree?.useOfSoldProducts?.items || [];
    if (items.length === 0) return null;
    return items
      .map((it) => {
        const lifespan = Number(it.averageExpectedLifeSpan) || 0;
        const sold = Number(it.numberOfItemsSold) || 0;
        const gasPerItem = Number(it.gasConsumptionItem) || 0;
        const elecPerItem = Number(it.electricityConsumptionItem) || 0;
        const lifetimeMultiplier = lifespan * sold;
        return {
          name: [it.itemBrand, it.itemDescription].filter(Boolean).join(" — ") || "Unspecified product",
          value: Number(it.totalEmission) || 0,
          gasKwh: gasPerItem * lifetimeMultiplier,
          electricityKwh: elecPerItem * lifetimeMultiplier,
          numberOfItemsSold: sold,
          averageExpectedLifeSpan: lifespan,
        };
      })
      .filter((r) => r.value > 0 || r.gasKwh > 0 || r.electricityKwh > 0)
      .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  }, [isCat11, cat5Report]);

  const totalCat11GasKwh = useMemo(() => {
    if (!cat11ProductRows) return 0;
    return cat11ProductRows.reduce((acc, r) => acc + (Number(r.gasKwh) || 0), 0);
  }, [cat11ProductRows]);
  const totalCat11ElectricityKwh = useMemo(() => {
    if (!cat11ProductRows) return 0;
    return cat11ProductRows.reduce((acc, r) => acc + (Number(r.electricityKwh) || 0), 0);
  }, [cat11ProductRows]);

  const totalCat9Consumption = useMemo(() => {
    if (!cat9TransportRows) return 0;
    return cat9TransportRows.reduce((acc, r) => acc + (Number(r.consumption) || 0), 0);
  }, [cat9TransportRows]);
  // If every Cat 9 row shares the same unit, surface it on the KPI footer;
  // otherwise we just show the total without a unit suffix.
  const cat9UnifiedUnit = useMemo(() => {
    if (!cat9TransportRows || cat9TransportRows.length === 0) return null;
    const units = new Set();
    cat9TransportRows.forEach((m) => { if (m.unit && !m.unitMixed) units.add(m.unit); });
    return units.size === 1 ? Array.from(units)[0] : null;
  }, [cat9TransportRows]);

  // Quarterly trend mode: one point per quarter across the same five years.
  // Queries only fire while the switch is on; the page's quarter filter is
  // ignored (all four quarters always plotted). Mirrors the other detail pages.
  const [trendQuarters, setTrendQuarters] = useState(false);
  const trendYears = useMemo(() => [year - 4, year - 3, year - 2, year - 1, year], [year]);
  const quarterlyScope3Queries = useGetQuarterlyMultiYearCompanyTotalEmissions(trendYears, {
    enabled: !!year && !!cat && trendQuarters,
    retry: false,
    cacheTime: 60_000,
  });

  // Trend across the last 5 years. Only periods with a real recorded value are
  // plotted; periods without data are omitted (no fabricated history).
  const trend = useMemo(() => {
    if (!cat) return [];
    if (trendQuarters) {
      const labels = quarterPeriodLabels(trendYears);
      return quarterlyScope3Queries
        .map((q, i) => {
          const v = valueFor(q.data);
          return { x: labels[i], y: v > 0 ? Number(v.toFixed(2)) : null };
        })
        .filter((p) => p.y != null);
    }
    const reals = [
      valueFor(yMinus4Scope3),
      valueFor(yMinus3Scope3),
      valueFor(yMinus2Scope3),
      valueFor(previousScope3),
      valueFor(currentScope3),
    ];
    return trendYears
      .map((yr, i) => ({ x: String(yr), y: reals[i] > 0 ? Number(reals[i].toFixed(2)) : null }))
      .filter((p) => p.y != null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, trendYears, trendQuarters, quarterlyScope3Queries, currentScope3, previousScope3, yMinus2Scope3, yMinus3Scope3, yMinus4Scope3]);

  // ESG-3960: the breakdown level, user-selectable here too. This page previously hard-coded auto
  // resolution and dropped the table at the lowest level.
  const {
    organizationStructure,
    autoLevelKey,
    levelKey: nextLevelKey,
    levelDimension: nextLevelDimension,
    levelName: dimensionLevelName,
    levelOptions,
    onSelectLevel,
    hideByDimension,
  } = useBreakdownLevel(dimension);

  // Real per-item Scope 3 totals at the next level — used to weight the
  // table rows so the totals tie up to what the dimension report says.
  const { data: dimensionReport } = useGetCompanyDimensionReports(
    nextLevelDimension?.id,
    {
      year,
      quarter: reportQuarter,
      page: 0,
      size: BY_DIMENSION_PAGE_SIZE,
      includeUnattributed: true,
      ...dimensionFilterParams(dimension),
    },
    { enabled: !!nextLevelDimension?.id && !!year && !!cat, staleTime: 0 }
  );
  // ESG-3960: a realTotalsByName map used to be built here and never read. Removed rather than rekeyed.

  // The "no {level} assigned" Scope 3 figure for this period.
  const unattributedScopeThree = useMemo(() => {
    const footer = (dimensionReport?.content || []).find((row) => row.unattributed);
    const value = Number(footer?.scopeThreeTotalCo2Emissions) || 0;
    return value > 0 ? value : null;
  }, [dimensionReport]);

  // Per-item rows for this category at the next-level dimension, from the real
  // per-dimension Scope 3 category breakdown (ESG-3942). Each item's value is
  // this category's emissions for that dimension item; items with no data for
  // the category are dropped (no fabricated slices).
  const categoryByItem = useMemo(() => {
    const m = new Map();
    (dimensionReport?.content || []).forEach((row) => {
      if (row.unattributed || row.dimensionItemId == null) return;
      const perCat = {};
      (row.scopeThreeCategories || []).forEach((c) => {
        perCat[c.category] = Number(c.co2Emissions) || 0;
      });
      m.set(String(row.dimensionItemId), perCat);
    });
    return m;
  }, [dimensionReport]);

  // ESG-3960: rows come from the report payload, which carries the id alongside the name. They used to be
  // seeded from the org-structure catalogue and joined to emissions BY NAME, which needed dedupeByName to
  // stop same-name twins rendering identical duplicate rows — a workaround that discarded one twin's real
  // figure rather than showing it.
  const dimensionRows = useMemo(() => {
    if (!cat || !nextLevelDimension || !scopeEnum) return [];
    return (dimensionReport?.content || [])
      .filter((row) => !row.unattributed && row.dimensionItemId != null)
      .map((row) => ({
        id: row.dimensionItemId,
        name: row.dimensionItem,
        value: categoryByItem.get(String(row.dimensionItemId))?.[scopeEnum] || 0,
      }))
      .filter((r) => r.value > 0)
      .sort((a, b) => b.value - a.value || (a.name || "").localeCompare(b.name || ""));
  }, [cat, nextLevelDimension, dimensionReport, categoryByItem, scopeEnum]);

  // For Cat 3 only: split the total purchased electricity across the same
  // dimension items, in the same proportions as the emissions split. The
  // CutTable will render this as a Consumption column + a totals row.
  const dimensionRowsWithConsumption = useMemo(() => {
    if (isCat3) {
      if (!purchasedElectricity || purchasedElectricity <= 0 || current <= 0) {
        return dimensionRows;
      }
      return dimensionRows.map((r) => ({
        ...r,
        consumption: (r.value / current) * purchasedElectricity,
      }));
    }
    // Cat 5: split the real company-level waste tonnes + wastewater m³ totals
    // across each dimension item, in proportion to its emissions share. Real
    // totals come from `/reports` (wastewater) and the paginated
    // `/reports/{id}/waste-generated-in-operations/items` aggregation. When a
    // real total isn't available the consumption column is left blank rather
    // than estimated.
    if (cat?.numberKey === "Five" && current > 0) {
      const hasRealWaste = totalWasteTonnesReal > 0;
      const hasRealWastewater = totalWastewaterM3 > 0;
      return dimensionRows.map((r) => {
        const share = current > 0 ? r.value / current : 0;
        return {
          ...r,
          waste: hasRealWaste ? totalWasteTonnesReal * share : null,
          wastewater: hasRealWastewater ? totalWastewaterM3 * share : null,
        };
      });
    }
    // Cat 11: split lifetime gas + electricity totals across the dimension rows
    // in proportion to each row's emissions share. There's no per-dimension
    // Cat 11 endpoint, so this apportions the real company-level totals.
    if (isCat11 && current > 0 && (totalCat11GasKwh > 0 || totalCat11ElectricityKwh > 0)) {
      return dimensionRows.map((r) => {
        const share = r.value / current;
        return {
          ...r,
          gasKwh: totalCat11GasKwh * share,
          electricityKwh: totalCat11ElectricityKwh * share,
        };
      });
    }
    return dimensionRows;
  }, [
    isCat3,
    purchasedElectricity,
    current,
    dimensionRows,
    cat,
    totalWasteTonnesReal,
    totalWastewaterM3,
    isCat11,
    totalCat11GasKwh,
    totalCat11ElectricityKwh,
  ]);

  const groupCutParams = { ...dimensionFilterParams(dimension), page: 0, size: 200 };

  // Cat 1/2 supplier + product cuts — real spend + recomputed emissions per group.
  const { data: supplierCut } = useGetScopeThreeGroupCuts(
    cat5ReportId, scopeEnum, { ...groupCutParams, groupBy: "SUPPLIER" },
    { enabled: !!cat5ReportId && !!scopeEnum && hasSupplierProductCut, retry: retryNot404, staleTime: 0 }
  );
  const { data: productCut } = useGetScopeThreeGroupCuts(
    cat5ReportId, scopeEnum, { ...groupCutParams, groupBy: "PRODUCT_CATEGORY" },
    { enabled: !!cat5ReportId && !!scopeEnum && hasSupplierProductCut, retry: retryNot404, staleTime: 0 }
  );
  // Cat 4 upstream transport-mode cut — real per-type emissions.
  const { data: transportModeCut } = useGetScopeThreeModeCuts(
    cat5ReportId, scopeEnum, dimensionFilterParams(dimension),
    { enabled: !!cat5ReportId && !!scopeEnum && hasModeCut, retry: retryNot404, staleTime: 0 }
  );

  // Map the backend cut DTOs to the CutTable row shape ({ name, value, spend? }).
  const supplierRows = useMemo(
    () => (supplierCut?.content || []).map((g) => ({ name: g.groupLabel, value: Number(g.co2Emissions) || 0, spend: Number(g.spentMoneyAmount) || 0 })),
    [supplierCut]
  );
  const productRows = useMemo(
    () => (productCut?.content || []).map((g) => ({ name: g.groupLabel, value: Number(g.co2Emissions) || 0, spend: Number(g.spentMoneyAmount) || 0 })),
    [productCut]
  );
  const transportRows = useMemo(
    () => (transportModeCut || []).map((m) => ({ name: m.modeLabel, value: Number(m.co2Emissions) || 0 })),
    [transportModeCut]
  );
  // Real per-activity waste rows from /reports/{id}/waste-generated-in-
  // operations/items — empty when no data is recorded (no fabricated fallback).
  const wasteTypeRows = wasteTypeRowsReal || [];

  if (!cat) {
    return (
      <Flex direction="column" gap={4}>
        <Flex>
          <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={handleBack}>
            Scope 3
          </Button>
        </Flex>
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.500">Category not found.</Text>
        </Box>
      </Flex>
    );
  }

  const topItem = dimensionRows[0];

  return (
    <Flex direction="column" gap={6}>
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={handleBack}>
          Scope 3
        </Button>
        <HStack spacing={2}>
          <IconButton
            aria-label="Previous category"
            icon={<CaretLeft size={12} />}
            w="28px"
            h="28px"
            minW="28px"
            p={0}
            bg="transparent"
            color="gray.600"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="full"
            _hover={{ bg: "gray.50", borderColor: "gray.300" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
            _focus={{ boxShadow: "none" }}
            onClick={() => prevCat && goToCategory(prevCat.numberKey)}
            isDisabled={!prevCat}
          />
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              borderRadius="full"
              border="1px solid"
              borderColor="gray.300"
              bg="transparent"
              color="gray.600"
              fontWeight="normal"
              rightIcon={<CaretDown size={14} />}
              _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            >
              {`Cat ${cat.index} · ${label}`}
            </MenuButton>
            <MenuList
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.200"
              boxShadow="md"
              py={1}
              maxH="320px"
              overflowY="auto"
              zIndex={2}
            >
              {scopeThreeCategories.map((c) => (
                <MenuItem
                  key={c.numberKey}
                  fontSize="sm"
                  color="gray.600"
                  py={1.5}
                  fontWeight={c.numberKey === numberKey ? "bold" : "normal"}
                  bg={c.numberKey === numberKey ? "gray.50" : undefined}
                  onClick={() => goToCategory(c.numberKey)}
                >
                  {`${c.index}. ${t(c.translationKey)}`}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <IconButton
            aria-label="Next category"
            icon={<CaretRight size={12} />}
            w="28px"
            h="28px"
            minW="28px"
            p={0}
            bg="transparent"
            color="gray.600"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="full"
            _hover={{ bg: "gray.50", borderColor: "gray.300" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
            _focus={{ boxShadow: "none" }}
            onClick={() => nextCat && goToCategory(nextCat.numberKey)}
            isDisabled={!nextCat}
          />
        </HStack>
      </Flex>

      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase" mb={1}>
            Scope 3 · Category {cat.index}
          </Text>
          <Text fontSize="40px" fontWeight="700" color="darkTeal" lineHeight="1.1">
            {label}
          </Text>
        </Box>
        <Flex gap={2} mt={2}>
          {setDimension && <DimensionDropdown selected={dimension || {}} setSelected={setDimension} />}
          {setYear && <YearDropdown setYear={setYear} selectedYear={year} />}
          {setQuarter && <QuarterDropdown setQuarter={setQuarter} selectedQuarter={quarter} />}
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: isCat1 && totalWaterUsageTco2 > 0 ? 4 : 3 }} spacing={6} mt={6}>
        <KpiCard
          accent={accent}
          title="Total"
          subtitle={`Cat ${cat.index} emissions`}
          value={current}
          pctChange={yoy}
          footerLeading={
            isCat3 && purchasedElectricity > 0 ? "From"
              : isCat9 && totalCat9Consumption > 0 ? "Across"
                : isCat11 && totalCat11GasKwh > 0 ? "Lifetime gas"
                  : undefined
          }
          footerKwh={
            isCat3 && purchasedElectricity > 0 ? purchasedElectricity
              : isCat9 && totalCat9Consumption > 0 ? totalCat9Consumption
                : isCat11 && totalCat11GasKwh > 0 ? totalCat11GasKwh
                  : undefined
          }
          footerTrailing={
            isCat3 && purchasedElectricity > 0 ? "kWh purchased"
              : isCat9 && totalCat9Consumption > 0
                ? (cat9UnifiedUnit ? `${cat9UnifiedUnit} freight` : "freight")
                : isCat11 && totalCat11GasKwh > 0 ? "kWh"
                  : undefined
          }
          extraFooters={
            isCat11 && totalCat11ElectricityKwh > 0
              ? [{ leading: "Lifetime electricity", value: totalCat11ElectricityKwh, trailing: "kWh" }]
              : undefined
          }
        />
        <KpiCard
          accent={accent}
          title="Share of Scope 3"
          subtitle="of company total"
          value={shareOfScope3}
          unit="%"
          format={(v) => `${v.toFixed(1)}`}
          pctChange={null}
        />
        <KpiCard
          accent={accent}
          title="Top dimension"
          subtitle={topItem?.name || "—"}
          value={topItem?.value}
          pctChange={null}
        />
        {/* ESG-4002: water usage is a real sibling section of Cat 1, already folded into the Total
            card above (ReportPurchasedGoodsAndServicesCalculationService adds it unconditionally) —
            this surfaces it, it doesn't add anything new to the total. */}
        {isCat1 && totalWaterUsageTco2 > 0 && (
          <KpiCard
            accent={accent}
            title="Water usage"
            subtitle="Included in Cat 1 total"
            value={totalWaterUsageTco2}
            pctChange={null}
            footerLeading="Consumption"
            footerKwh={totalWaterUsageM3}
            footerTrailing="m³"
          />
        )}
      </SimpleGrid>

      <TrendChart
        trend={trend}
        accent={accent}
        categoryLabel={label}
        showQuarters={trendQuarters}
        setShowQuarters={setTrendQuarters}
        year={year}
        cat={cat}
      />

      <CategoryCuts
        category={cat}
        accent={accent}
        cutTabs={cutTabs}
        dimensionRows={dimensionRowsWithConsumption}
        nextLevelName={dimensionLevelName}
        hideByDimension={hideByDimension}
        unattributed={unattributedScopeThree}
        levelOptions={levelOptions}
        selectedLevelKey={nextLevelKey}
        autoLevelKey={autoLevelKey}
        onSelectLevel={onSelectLevel}
        supplierRows={supplierRows}
        productRows={productRows}
        transportRows={transportRows}
        wasteTypeRows={wasteTypeRows}
        wasteRawItems={isCat5 ? (wasteItemsPage?.content || []) : null}
        wastewaterTotalEmissions={totalWastewaterTco2}
        wastewaterTotalConsumption={totalWastewaterM3}
        businessTravelRows={travelModeRows}
        cat9TransportRows={cat9TransportRows}
        year={year}
        consumptionUnit={isCat3 ? "kWh" : undefined}
        consumptionColumns={
          cat?.numberKey === "Five"
            ? [
                { key: "waste", unit: "t", label: "Waste (t)" },
                { key: "wastewater", unit: "m³", label: "Wastewater (m³)" },
              ]
            : isCat11
              ? [
                  { key: "gasKwh", unit: "kWh", label: "Gas (kWh)" },
                  { key: "electricityKwh", unit: "kWh", label: "Electricity (kWh)" },
                ]
              : undefined
        }
      />

      <SourceDataTasks
        scope={scopeEnum}
        year={year}
        quarter={quarter}
        dimension={dimension}
        organizationStructure={organizationStructure}
      />
    </Flex>
  );
};

// Same inline-block wrapper pattern as Scope 1's KpiCard so the number
// renders at the full clamp size and the chip+unit cluster stays flush
// against its right edge. Optional footer (footerLeading + footerKwh +
// footerTrailing) mirrors the Scope 2 KpiCard pattern — used on Cat 3
// to surface purchased-electricity consumption alongside the emissions.
const KpiCard = ({
  accent,
  title,
  subtitle,
  value,
  pctChange,
  unit = "tCO₂e",
  format,
  footerLeading,
  footerKwh,
  footerTrailing,
  extraFooters,
}) => {
  const display = format ? format(value || 0) : (value != null ? formatTco2(value) : null);
  const formatNumber = (v) =>
    v != null && Number.isFinite(Number(v))
      ? Number(v).toLocaleString("en-GB", { maximumFractionDigits: 0 })
      : null;
  const formattedKwh = formatNumber(footerKwh);
  // Combine the legacy single-footer signature with the new multi-line
  // shape so callers don't have to choose. The primary footer (footerKwh)
  // renders first; extraFooters render below.
  const footers = [];
  if (formattedKwh || footerLeading || footerTrailing) {
    footers.push({ leading: footerLeading, value: formattedKwh, trailing: footerTrailing });
  }
  (extraFooters || []).forEach((f) => {
    const formatted = formatNumber(f?.value);
    if (formatted || f?.leading || f?.trailing) {
      footers.push({ leading: f.leading, value: formatted, trailing: f.trailing });
    }
  });
  return (
    <Box position="relative" pt={3} minW={0} overflow="hidden">
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        <Text fontSize="13px" color="gray.500" noOfLines={1}>{subtitle}</Text>
      </HStack>
      <Box
        position="relative"
        display="inline-block"
        whiteSpace="nowrap"
        sx={{ "--big": "clamp(24px, 2.6vw, 56px)" }}
      >
        <Flex align="flex-end" gap={3}>
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color="darkTeal"
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {display ?? "—"}
          </Text>
          <Flex direction="column" justify="space-between" alignSelf="stretch" flexShrink={0}>
            {pctChange != null ? <YoYChip pctChange={pctChange} /> : <Box />}
            <Text fontSize="calc(var(--big) * 0.36)" fontWeight="600" color="gray.600" lineHeight="1">
              {unit}
            </Text>
          </Flex>
        </Flex>
      </Box>
      {footers.length > 0 && (
        <Flex direction="column" mt={3} gap={1}>
          {footers.map((f, i) => (
            <HStack key={i} spacing={2} align="baseline" flexWrap="wrap">
              {f.leading && (
                <Text fontSize="13px" color="gray.500">{f.leading}</Text>
              )}
              {f.value && (
                <Text fontSize="22px" fontWeight="700" color="darkTeal" lineHeight="1">
                  {f.value}
                </Text>
              )}
              {f.trailing && (
                <Text fontSize="13px" color="gray.500">{f.trailing}</Text>
              )}
            </HStack>
          ))}
        </Flex>
      )}
    </Box>
  );
};

// Quarter-by-quarter table of this category's emissions (a single row).
// Mounted only while the Table view is selected. Org-wide, same source as
// the trend (/reports/total-emissions per quarter).
const CategoryQuarterlyTable = ({ year, cat, categoryLabel }) => {
  const compare = useQuarterlyTableCompare();
  const currentQueries = useGetQuarterlyCompanyTotalEmissions(year, {
    retry: false,
    cacheTime: 60_000,
  });
  const prevQueries = useGetQuarterlyCompanyTotalEmissions(year - 1, {
    retry: false,
    cacheTime: 60_000,
    enabled: compare.needsPreviousYear,
  });
  const get = (d) => Number(d?.[`category${cat.numberKey}TotalCo2Emissions`]) || null;
  const rows = [
    {
      label: categoryLabel,
      bold: true,
      values: currentQueries.map((q) => (q.status === "success" ? get(q.data) : null)),
      prevValues: prevQueries.map((q) => (q.status === "success" ? get(q.data) : null)),
    },
  ];
  const isLoading =
    currentQueries.some((q) => q.isLoading) ||
    (compare.needsPreviousYear && prevQueries.some((q) => q.isLoading));

  return <QuarterlyBreakdownTable year={year} rows={rows} isLoading={isLoading} compare={compare} />;
};

// Trend / Table pill for the category page (it has no sankey flow view).
const CategoryViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

const TrendChart = ({ trend, accent, categoryLabel, showQuarters = false, setShowQuarters, year, cat }) => {
  const [view, setView] = useState("trend");
  const isTable = view === "table";
  const hasData = trend && trend.length > 0;
  // Keep the header (and the switches) rendered when quarterly mode comes up
  // empty or the table view is active — otherwise the user can't switch back.
  if (!hasData && !showQuarters && !isTable) return null;
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={2} flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="22px" fontWeight="700" color="darkTeal">
            {isTable ? "Emissions by quarter" : "5-year trend"}
          </Text>
          <Text fontSize="13px" color="gray.500">
            {isTable
              ? `${categoryLabel} emissions for ${year}, split by quarter with a full-year total.`
              : `${categoryLabel} emissions across the last five reporting years${showQuarters ? ", split by quarter" : ""}.`}
          </Text>
        </Box>
        <HStack spacing={3}>
          {!isTable && setShowQuarters && (
            <QuartersSwitch isChecked={showQuarters} onChange={setShowQuarters} />
          )}
          <CategoryViewToggle view={view} setView={setView} />
        </HStack>
      </Flex>
      {isTable ? (
        <CategoryQuarterlyTable year={year} cat={cat} categoryLabel={categoryLabel} />
      ) : !hasData ? (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">
            No quarterly data for this category — emissions for these years were reported at the yearly level only.
          </Text>
        </Box>
      ) : (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="320px">
        <ResponsiveLine
          data={[{ id: categoryLabel, data: trend }]}
          layers={["grid", "markers", "axes", axisBaselineLayer, "areas", "crosshair", "lines", "points", "slices", "mesh", "legends"]}
          margin={{ top: 12, right: 24, bottom: showQuarters ? 56 : 36, left: 64 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
          colors={[accent]}
          curve="monotoneX"
          enableArea
          areaOpacity={0.12}
          enablePoints
          pointSize={6}
          pointBorderWidth={2}
          pointBorderColor={accent}
          pointColor="#FFFFFF"
          axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: showQuarters ? -45 : 0 }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          }}
          enableGridX={false}
          theme={chartTheme}
          tooltip={({ point }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{point.data.x}</Text>
              <Text color={accent}>{formatTco2(point.data.y)} tCO₂e</Text>
            </Box>
          )}
          useMesh
        />
      </Box>
      )}
      {hasData && !isTable && (
        <Text fontSize="11px" color="gray.500" mt={2}>
          {showQuarters
            ? "Only quarters with recorded data are plotted — years reported at the yearly level only will not appear."
            : "Years without recorded data are shown with illustrative figures so the trend isn't broken."}
        </Text>
      )}
    </Box>
  );
};

// Generic cut table — used for By-dimension, By-supplier, By-product etc.
// Same look as the Scope 1 by-dimension table: header band with accent square,
// row per item, emissions + inline YoY, share-of-category bar.
//
// Consumption columns:
// - `consumptionUnit` (string): single column, reads from row.consumption.
// - `consumptionColumns` (array of {key, unit?, label?, perRowUnit?}): one or more columns, each
//   pulling from row[key]. Used by Cat 3/9/11 and the dimension tab for fixed-unit columns.
// - `perRowUnit: true` on a column: each row supplies its own `row.unit` instead of the column
//   having one fixed unit — used by Cat 5's Waste type table, where real activity rows are tonnes
//   and the Wastewater row is m³. The Total cell shows "Mixed units" rather than summing across
//   units that don't add up.
// Both modes append a Total row at the bottom.
const formatKwh = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return Number(value).toLocaleString("en-GB", { maximumFractionDigits: 0 });
};

const CutTable = ({
  category,
  accent,
  rows,
  headerLabel,
  description,
  year,
  cutKey,
  consumptionUnit,
  consumptionColumns,
}) => {
  if (!rows || rows.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">No data for this view.</Text>
      </Box>
    );
  }
  // Normalise to the multi-column shape so the rendering loop has one path.
  const columns = consumptionColumns
    || (consumptionUnit ? [{ key: "consumption", unit: consumptionUnit }] : []);
  const showConsumption = columns.length > 0;
  const totalEmissions = rows.reduce((acc, r) => acc + r.value, 0);
  const columnTotals = columns.map((col) =>
    rows.reduce((acc, r) => acc + (Number(r[col.key]) || 0), 0)
  );
  // `col.perRowUnit`: each row supplies its own `row.unit` instead of one fixed unit for the whole
  // column (Cat 5 waste type — tonnes for real activity rows, m³ for the Wastewater row). Summing
  // across mixed units would be meaningless, so the Total cell shows a figure only when every row
  // that has a value shares the same unit; otherwise it shows "mixed" (same rule as the Cat 9/travel
  // mode tables' totalUnitMixed).
  const columnUnitMixed = columns.map((col) => {
    if (!col.perRowUnit) return false;
    const units = new Set(rows.filter((r) => (Number(r[col.key]) || 0) > 0).map((r) => r.unit || col.unit));
    return units.size > 1;
  });

  return (
    <Box>
      {description && (
        <Text fontSize="13px" color="gray.500" mb={3}>{description}</Text>
      )}
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={accent} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                  <Text>{(headerLabel || "ITEM").toUpperCase()}</Text>
                </HStack>
              </Th>
              {columns.map((col) => (
                <Th key={col.key} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                  {col.label || `Consumption (${col.unit})`}
                </Th>
              ))}
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">{`Share of ${category.index}. ${category.label || ""}`}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => {
              const share = totalEmissions > 0 ? row.value / totalEmissions : 0;
              return (
                <Tr key={row.name} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                  <Td py={3} pl={4} pr={1} width="32px">
                    <Box w="20px" />
                  </Td>
                  <Td py={3} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                    <HStack spacing={2}>
                      <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                      <Text>{row.name}</Text>
                    </HStack>
                  </Td>
                  {columns.map((col) => (
                    <Td key={col.key} py={3} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                      {(col.format || formatKwh)(row[col.key])}
                      {col.perRowUnit && row[col.key] > 0 && (
                        <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="11px">{row.unit || col.unit}</Text>
                      )}
                    </Td>
                  ))}
                  <Td py={3} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                    {formatTco2(row.value)}
                  </Td>
                  <Td py={3} px={4} textAlign="left" minW="220px">
                    <HStack spacing={3}>
                      <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box h="100%" w={`${share * 100}%`} bg={accent} borderRadius="full" />
                      </Box>
                      <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                        {(share * 100).toFixed(1)}%
                      </Text>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} pl={4} pr={1} width="32px" />
              <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              {columns.map((col, i) => (
                <Td key={col.key} py={4} px={4} fontSize="14px" fontWeight="700" color="darkTeal" textAlign="right">
                  {columnUnitMixed[i] ? (
                    <Text as="span" color="gray.500" fontWeight="500" fontSize="12px">Mixed units</Text>
                  ) : (
                    <>
                      {(col.format || formatKwh)(columnTotals[i])}
                      {col.unit && (
                        <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="11px">{col.unit}</Text>
                      )}
                    </>
                  )}
                </Td>
              ))}
              <Td py={4} px={4} fontSize="14px" fontWeight="700" color="darkTeal" textAlign="right">
                {formatTco2(totalEmissions)}
              </Td>
              <Td py={4} px={4} textAlign="left" minW="220px">
                <HStack spacing={3}>
                  <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box h="100%" w="100%" bg="darkTeal" borderRadius="full" />
                  </Box>
                  <Text fontSize="13px" color="darkTeal" fontWeight="700" minW="48px" textAlign="right">100.0%</Text>
                </HStack>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// Cat 6 — Mode-of-transport breakdown with expandable subtype rows.
// Top-level row = mode (Cars, Flights, Rail, …). Click to expand the
// real subtype rows underneath. Each level shows km travelled (back-
// calculated from emissions × the typical kg-CO₂e/km intensity) and
// emissions, plus a share-of-category bar in the same style as the
// dimension cut.
const BusinessTravelModeTable = ({
  category,
  accent,
  rows,
  consumptionField = "consumption",
  consumptionLabel = "Consumption",
  consumptionUnit = "",
  headerLabel = "Mode of transport",
  subHeaderLabel = "Sub-type",
  description = "Business travel emissions split by mode of transport, with sub-types underneath.",
  showRowUnits = false,
  showConsumption = true,
}) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  if (!rows || rows.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No data has been entered for this year.
        </Text>
      </Box>
    );
  }
  // Only rows with sub-rows are expandable; the control acts on those.
  const expandableNames = rows.filter((r) => (r.subRows || []).length > 0).map((r) => r.name);
  const openCount = expandableNames.filter((n) => expanded[n]).length;
  const expandAll = () => setExpanded(Object.fromEntries(expandableNames.map((n) => [n, true])));
  const collapseAll = () => setExpanded({});
  const totalEmissions = rows.reduce((acc, r) => acc + (Number(r.value) || 0), 0);
  const totalConsumption = rows.reduce((acc, r) => acc + (Number(r[consumptionField]) || 0), 0);
  // When per-row units differ (Cat 9), don't try to print a single unit
  // beside the grand total — just fall back to the column header's unit.
  const totalUnitMixed = showRowUnits
    && new Set(rows.filter((r) => r.unit && !r.unitMixed).map((r) => r.unit)).size > 1;
  const totalUnit = totalUnitMixed ? "mixed" : consumptionUnit;
  const consumptionHeader = `${consumptionLabel} (${consumptionUnit})`;
  const formatConsumption = (v) => Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });
  const rowUnitSuffix = (r) => (showRowUnits ? (r.unitMixed ? "mixed" : (r.unit || "")) : "");

  return (
    <Box>
      <Text fontSize="13px" color="gray.500" mb={3}>{description}</Text>
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={accent} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                  <Text>{headerLabel}</Text>
                  {expandableNames.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === expandableNames.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              {showConsumption && (
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                  {consumptionHeader}
                </Th>
              )}
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                Emissions (tCO₂e)
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
                {`Share of ${category.index}. ${category.label || ""}`}
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => {
              const isOpen = !!expanded[row.name];
              const share = totalEmissions > 0 ? row.value / totalEmissions : 0;
              const hasSubs = (row.subRows || []).length > 0;
              const rowUnit = rowUnitSuffix(row);
              return (
                <Fragment key={row.name}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={hasSubs ? "pointer" : "default"}
                    _hover={hasSubs ? { bg: "gray.50" } : undefined}
                    onClick={hasSubs ? () => toggle(row.name) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {hasSubs ? (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(row.name); }}
                        />
                      ) : (
                        <Box w="20px" />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                      <HStack spacing={2}>
                        <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                        <Text>{row.name}</Text>
                      </HStack>
                    </Td>
                    {showConsumption && (
                      <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                        {formatConsumption(row[consumptionField])}
                        {showRowUnits && rowUnit && (
                          <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="11px">{rowUnit}</Text>
                        )}
                      </Td>
                    )}
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                      {formatTco2(row.value)}
                    </Td>
                    <Td py={4} px={4} textAlign="left" minW="220px">
                      <HStack spacing={3}>
                        <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                          <Box h="100%" w={`${share * 100}%`} bg={accent} borderRadius="full" />
                        </Box>
                        <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                          {(share * 100).toFixed(1)}%
                        </Text>
                      </HStack>
                    </Td>
                  </Tr>
                  {isOpen && hasSubs && (
                    <>
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">{subHeaderLabel}</Td>
                        {showConsumption && (
                          <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">{consumptionHeader}</Td>
                        )}
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of {row.name}</Td>
                      </Tr>
                      {row.subRows.map((sub) => {
                        const subShare = row.value > 0 ? sub.value / row.value : 0;
                        const subUnit = rowUnitSuffix(sub);
                        return (
                          <Tr key={`${row.name}-${sub.name}`} bg="gray.50">
                            <Td py={2} pl={4} pr={1} width="32px" />
                            <Td py={2} pl={1} pr={4} fontSize="13px" color="gray.700">
                              <HStack spacing={2}>
                                <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                                <Text>{sub.name}</Text>
                              </HStack>
                            </Td>
                            {showConsumption && (
                              <Td py={2} px={4} fontSize="13px" color="gray.700" textAlign="right">
                                {formatConsumption(sub[consumptionField])}
                                {showRowUnits && subUnit && (
                                  <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="10px">{subUnit}</Text>
                                )}
                              </Td>
                            )}
                            <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">
                              {formatTco2(sub.value)}
                            </Td>
                            <Td py={2} px={4} textAlign="left" minW="220px">
                              <HStack spacing={3}>
                                <Box flex="1" h="5px" bg="gray.100" borderRadius="full" overflow="hidden">
                                  <Box h="100%" w={`${subShare * 100}%`} bg={accent} borderRadius="full" />
                                </Box>
                                <Text fontSize="12px" color="gray.700" fontWeight="600" minW="44px" textAlign="right">
                                  {(subShare * 100).toFixed(1)}%
                                </Text>
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </>
                  )}
                </Fragment>
              );
            })}
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} pl={4} pr={1} width="32px" />
              <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              {showConsumption && (
                <Td py={4} px={4} fontSize="14px" fontWeight="700" color="darkTeal" textAlign="right">
                  {formatConsumption(totalConsumption)}
                  <Text as="span" color="gray.500" fontWeight="500" ml={1} fontSize="11px">{totalUnit}</Text>
                </Td>
              )}
              <Td py={4} px={4} fontSize="14px" fontWeight="700" color="darkTeal" textAlign="right">
                {formatTco2(totalEmissions)}
              </Td>
              <Td py={4} px={4} textAlign="left" minW="220px">
                <HStack spacing={3}>
                  <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                    <Box h="100%" w="100%" bg="darkTeal" borderRadius="full" />
                  </Box>
                  <Text fontSize="13px" color="darkTeal" fontWeight="700" minW="48px" textAlign="right">100.0%</Text>
                </HStack>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// Decide which cut tabs apply to a given category. Cat 1/2 get supplier
// and product cuts; Cat 4 (Upstream transport & distribution) gets a
// transport-mode cut alongside the dimension cut. Other categories still
// show the dimension cut only.
const CAT_CUT_CONFIG = {
  One: { tabs: ["dimension", "supplier", "product"] },
  Two: { tabs: ["dimension", "supplier", "product"] },
  Four: { tabs: ["dimension", "transport"] },
  Five: { tabs: ["dimension", "wasteType"] },
  Six: { tabs: ["dimension", "businessTravelMode"] },
  Seven: { tabs: ["dimension", "businessTravelMode"] },
  Nine: { tabs: ["dimension", "transportType"] },
};
const cutTabsForCategory = (cat) => CAT_CUT_CONFIG[cat?.numberKey]?.tabs || ["dimension"];

// Pill toggle that swaps the Cat 5 Waste type cut between the summary
// table and a 3-tier sankey of wasteActivity → wasteType → disposalMethod.
// Same shape as the existing Summary/Visual toggles elsewhere.
const WasteTypeViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Summary", value: "summary", icon: <ListBullets size={14} /> },
    { label: "Flow", value: "flow", icon: <FlowArrow size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Stable colour palette for sankey columns. Activity nodes use the
// category accent (purple), waste types use mid-blue, disposal methods
// use a teal. Keeps the diagram readable without depending on the
// per-category accent.
const WASTE_FLOW_COL_COLORS = {
  activity: "#8B5CF6",
  type: "#3B82F6",
  disposal: "#14B8A6",
};

// 3-tier sankey for Cat 5 — Waste activity → Waste type → Disposal
// method. Wastewater is appended as its own activity node feeding a
// single "Wastewater" type and "Treatment" disposal so the totals
// reconcile to the Cat 5 headline (waste + wastewater).
const WasteFlowSankey = ({ accent, items, wastewaterEmissions, wastewaterConsumption }) => {
  const { nodes, links, nodeLabel, grandTotal } = useMemo(() => {
    const built = { nodes: [], links: [] };
    const seen = new Set();
    const addNode = (id, color) => {
      if (seen.has(id)) return;
      seen.add(id);
      built.nodes.push({ id, nodeColor: color });
    };

    // Aggregate (activity, type, disposal) → emissions across all items.
    const cells = new Map();
    (items || []).forEach((it) => {
      const activity = prettyActivity(it.wasteActivity || "Unspecified");
      const wasteType = prettyActivity(it.wasteType || "Unspecified");
      const disposal = prettyActivity(it.disposalMethod || "Unspecified");
      const value = Number(it.co2Emissions) || 0;
      if (value <= 0) return;
      const key = `${activity}||${wasteType}||${disposal}`;
      cells.set(key, (cells.get(key) || 0) + value);
    });

    // Activity nodes are unique per activity name; waste types are
    // disambiguated by activity (so the same "Mixed" name under two
    // activities doesn't collapse and lose lineage to its source);
    // disposal methods are deduplicated globally so all Landfill flows —
    // regardless of activity/type — merge into a single right-column
    // rectangle. The right-column totals therefore add up to a true
    // disposal total. Crossings between middle and right columns are
    // accepted as the trade-off for that deduplication.
    const labelMap = new Map();
    cells.forEach((value, key) => {
      const [activity, wasteType, disposal] = key.split("||");
      const activityId = `A:${activity}`;
      const typeId = `T:${activity}::${wasteType}`;
      const disposalId = `D:${disposal}`;
      addNode(activityId, WASTE_FLOW_COL_COLORS.activity);
      addNode(typeId, WASTE_FLOW_COL_COLORS.type);
      addNode(disposalId, WASTE_FLOW_COL_COLORS.disposal);
      labelMap.set(activityId, activity);
      labelMap.set(typeId, wasteType);
      labelMap.set(disposalId, disposal);
      built.links.push({ source: activityId, target: typeId, value });
      built.links.push({ source: typeId, target: disposalId, value });
    });

    // Wastewater branch — Activity "Wastewater" → Type "Wastewater" →
    // shared "Treatment" disposal node so it joins any real Treatment
    // disposal flows from the waste items.
    if ((wastewaterEmissions || 0) > 0) {
      const a = "A:Wastewater";
      const t = "T:Wastewater::Wastewater";
      const d = "D:Treatment";
      addNode(a, WASTE_FLOW_COL_COLORS.activity);
      addNode(t, WASTE_FLOW_COL_COLORS.type);
      addNode(d, WASTE_FLOW_COL_COLORS.disposal);
      labelMap.set(a, "Wastewater");
      labelMap.set(t, "Wastewater");
      labelMap.set(d, "Treatment");
      const v = Number(wastewaterEmissions) || 0;
      built.links.push({ source: a, target: t, value: v });
      built.links.push({ source: t, target: d, value: v });
    }

    // Grand total = sum of every flow leaving the left column, computed on
    // the original "A:" ids before the Safari-safe re-keying below.
    const total = built.links
      .filter((l) => String(l.source).startsWith("A:"))
      .reduce((acc, l) => acc + (Number(l.value) || 0), 0);

    const safe = toSafariSafeSankey(built.nodes, built.links, (id) => labelMap.get(id) || id);
    return { nodes: safe.nodes, links: safe.links, nodeLabel: safe.labels, grandTotal: total };
  }, [items, wastewaterEmissions]);

  if (!nodes || nodes.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No waste data has been entered for this year.
        </Text>
      </Box>
    );
  }

  // Cap the chart height by the number of left-column nodes so a small
  // dataset doesn't render absurdly tall rectangles.
  const leftNodeCount = nodes.filter((n) => String(n.origId).startsWith("A:")).length;
  const heightPx = Math.max(360, Math.min(620, leftNodeCount * 60 + 120));

  const formatPct = (numerator, denominator) => {
    if (!denominator || denominator <= 0) return null;
    return `${((numerator / denominator) * 100).toFixed(1)}%`;
  };

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={`${heightPx}px`}>
        <ResponsiveSankey
          data={{ nodes: nodes.map(({ id, nodeColor }) => ({ id, nodeColor })), links }}
          margin={{ top: 12, right: 240, bottom: 12, left: 200 }}
          align="justify"
          sort="input"
          colors={(node) => node.nodeColor}
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={14}
          nodeSpacing={14}
          nodeBorderWidth={0}
          nodeBorderRadius={2}
          linkOpacity={0.45}
          linkHoverOthersOpacity={0.1}
          linkContract={2}
          enableLinkGradient
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={8}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          label={(node) => nodeLabel.get(node.id) || node.id}
          theme={chartTheme}
          nodeTooltip={({ node }) => {
            const pct = formatPct(node.value, grandTotal);
            return (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                <Text fontWeight="700" color="gray.800">{nodeLabel.get(node.id) || node.id}</Text>
                <Text color="gray.600">
                  {Number(node.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
                  {pct && (
                    <Text as="span" color="gray.500" ml={2}>· {pct} of Cat 5</Text>
                  )}
                </Text>
              </Box>
            );
          }}
          linkTooltip={({ link }) => {
            const sourceVal = Number(link.source?.value) || 0;
            const pctOfSource = formatPct(link.value, sourceVal);
            const pctOfTotal = formatPct(link.value, grandTotal);
            return (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                <Text fontWeight="700" color="gray.800">
                  {(nodeLabel.get(link.source.id) || link.source.id)} → {(nodeLabel.get(link.target.id) || link.target.id)}
                </Text>
                <Text color="gray.600">
                  {Number(link.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
                </Text>
                {(pctOfSource || pctOfTotal) && (
                  <Text color="gray.500" fontSize="11px">
                    {pctOfSource && <>{pctOfSource} of {nodeLabel.get(link.source.id) || link.source.id}</>}
                    {pctOfSource && pctOfTotal && " · "}
                    {pctOfTotal && <>{pctOfTotal} of Cat 5</>}
                  </Text>
                )}
              </Box>
            );
          }}
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Flow widths are tCO₂e. Wastewater is shown as its own activity → type → disposal trio so its emissions reconcile with the Cat 5 headline.
      </Text>
    </Box>
  );
};

// Tab strip wrapper. Single-tab categories render the cut directly with no
// tab chrome — keeps the visual weight similar to the previous version. Cat 1
// (and anything else added to CAT_CUT_CONFIG) gets a real tab strip.
const CategoryCuts = ({
  category,
  accent,
  cutTabs,
  dimensionRows,
  nextLevelName,
  hideByDimension,
  unattributed = null,
  levelOptions = [],
  selectedLevelKey = null,
  autoLevelKey = null,
  onSelectLevel = null,
  supplierRows,
  productRows,
  transportRows,
  wasteTypeRows,
  wasteRawItems,
  wastewaterTotalEmissions,
  wastewaterTotalConsumption,
  businessTravelRows,
  cat9TransportRows,
  year,
  consumptionUnit,
  consumptionColumns,
}) => {
  // Cat 5 wasteType cut has a Summary (table) / Flow (sankey) sub-toggle.
  // Default to Summary so the page reads the same as before.
  const [wasteTypeView, setWasteTypeView] = useState("summary");
  // Build the panel for a given cut key. Centralised so behaviour stays
  // consistent across direct-render and tabbed render.
  const renderCut = (key) => {
    if (key === "dimension") {
      // ESG-3960: only hidden when the org has no dimensions at all. It used to be hidden whenever the
      // filter sat at the lowest level ("no further breakdown"), which removed the table rather than
      // letting the user choose a level.
      if (hideByDimension) {
        return (
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={6}>
            <Text textAlign="center" color="gray.500" fontSize="13px">
              No dimensions are configured for this organisation.
            </Text>
          </Box>
        );
      }
      return (
        <>
          {/* ESG-3960: the level selector, ported from the Scope 1/2 drill-downs. Without it this page
              could only ever show the level auto-resolution picked, which at the staging org's level-four
              dimension is a 1,807-item table. */}
          {onSelectLevel && levelOptions.length > 0 && (
            <Flex justify="flex-end" mb={2}>
              <Menu>
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="ghost"
                  color="gray.600"
                  fontWeight="600"
                  rightIcon={<CaretDown size={14} weight="bold" />}
                  _hover={{ bg: "gray.50" }}
                >
                  {`By ${nextLevelName ? nextLevelName.toLowerCase() : "dimension"}`}
                </MenuButton>
                <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
                  <MenuItem
                    onClick={() => onSelectLevel(null)}
                    fontWeight={selectedLevelKey === autoLevelKey ? "bold" : "normal"}
                  >
                    <Flex align="center" gap={2} w="full" justify="space-between">
                      <Text>Auto</Text>
                      <Text fontSize="11px" color="gray.400">level below filter</Text>
                    </Flex>
                  </MenuItem>
                  {levelOptions.map((opt) => (
                    <MenuItem
                      key={opt.key}
                      onClick={() => onSelectLevel(opt.key)}
                      fontWeight={selectedLevelKey === opt.key ? "bold" : "normal"}
                    >
                      {opt.name}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          )}
          <CutTable
            category={category}
            accent={accent}
            rows={dimensionRows}
            headerLabel={nextLevelName || "Dimension"}
            description={
              unattributed != null
                ? `${category.index}. ${category.label} emissions across each ${nextLevelName?.toLowerCase() || "next-level"} item. ${formatTco2(unattributed)} tCO₂e of Scope 3 carries no ${nextLevelName?.toLowerCase() || "dimension"} and is not in the rows below.`
                : `${category.index}. ${category.label} emissions across each ${nextLevelName?.toLowerCase() || "next-level"} item. Use the dimension filter above to drill deeper.`
            }
            year={year}
            cutKey="dimension"
            consumptionUnit={consumptionColumns ? undefined : consumptionUnit}
            consumptionColumns={consumptionColumns}
          />
        </>
      );
    }
    if (key === "supplier") {
      return (
        <CutTable
          category={category}
          accent={accent}
          rows={supplierRows}
          headerLabel="Supplier"
          description="Top suppliers by emissions for this category, with the procurement spend behind them, for the selected year and dimension filter."
          year={year}
          cutKey="supplier"
          consumptionColumns={[SPEND_COLUMN]}
        />
      );
    }
    if (key === "product") {
      return (
        <CutTable
          category={category}
          accent={accent}
          rows={productRows}
          headerLabel="Product or service type"
          description="Emissions and procurement spend grouped by product/service category, for the selected year and dimension filter."
          year={year}
          cutKey="product"
          consumptionColumns={[SPEND_COLUMN]}
        />
      );
    }
    if (key === "transport") {
      return (
        <CutTable
          category={category}
          accent={accent}
          rows={transportRows}
          headerLabel="Type of transport"
          description="Upstream transport emissions split by type of transport, for the selected year and dimension filter."
          year={year}
          cutKey="transport"
        />
      );
    }
    if (key === "businessTravelMode") {
      return (
        <BusinessTravelModeTable
          category={category}
          accent={accent}
          rows={businessTravelRows}
          showConsumption={false}
          description="Emissions split by mode of transport, with sub-types underneath, for the selected year and dimension filter."
        />
      );
    }
    if (key === "transportType") {
      return (
        <BusinessTravelModeTable
          category={category}
          accent={accent}
          rows={cat9TransportRows}
          consumptionField="consumption"
          consumptionLabel="Consumption"
          headerLabel="Type of transport"
          subHeaderLabel="Sub-type"
          description="Downstream transport split by type of transport, with sub-types underneath. Consumption and units come straight from the recorded data — sub-rows show the unit per group, falling back to mixed when multiple units appear within the same node."
          showRowUnits
        />
      );
    }
    if (key === "wasteType") {
      const isFlow = wasteTypeView === "flow";
      return (
        <Box>
          <Flex justify="flex-end" mb={3}>
            <WasteTypeViewToggle view={wasteTypeView} setView={setWasteTypeView} />
          </Flex>
          {isFlow ? (
            <WasteFlowSankey
              accent={accent}
              items={wasteRawItems}
              wastewaterEmissions={wastewaterTotalEmissions}
              wastewaterConsumption={wastewaterTotalConsumption}
            />
          ) : (
            <CutTable
              category={category}
              accent={accent}
              rows={wasteTypeRows}
              headerLabel="Waste type"
              description="Waste generated in operations split by activity, with wastewater listed as its own row below. Each row shows consumption in its own unit — tonnes for waste activities, m³ for wastewater."
              year={year}
              cutKey="wasteType"
              consumptionColumns={[
                { key: "consumption", label: "Consumption", perRowUnit: true },
              ]}
            />
          )}
        </Box>
      );
    }
    return null;
  };

  // Single cut → no tabs, just the panel with its heading.
  if (!cutTabs || cutTabs.length <= 1) {
    const only = cutTabs?.[0];
    return (
      <Box>
        <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>
          {only === "supplier"
            ? "By supplier"
            : only === "product"
              ? "By product or service type"
              : only === "transport"
                ? "By transport mode"
                : only === "wasteType"
                  ? "By waste type"
                  : only === "businessTravelMode"
                    ? "By mode of transport"
                    : only === "transportType"
                      ? "By type of transport"
                      : `By ${nextLevelName?.toLowerCase() || "dimension"}`}
        </Text>
        {renderCut(only || "dimension")}
      </Box>
    );
  }

  return (
    <Box>
      <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>Breakdown</Text>
      <Tabs variant="primary" isLazy>
        <TabList>
          {cutTabs.map((key) => (
            <Tab key={key}>
              {key === "dimension"
                ? (nextLevelName || "Dimension")
                : key === "supplier"
                  ? "Supplier"
                  : key === "transport"
                    ? "Transport mode"
                    : key === "wasteType"
                      ? "Waste type"
                      : key === "businessTravelMode"
                        ? "Mode of transport"
                        : key === "transportType"
                          ? "Type of transport"
                          : "Product or service type"}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {cutTabs.map((key) => (
            <TabPanel key={key} px={0} pt={6}>
              {renderCut(key)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
export default ScopeThreeCategoryDetail;
